import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { IconBoard, IconUser, MainMenu } from '@bbl-digital/snorre'
import {
  AppPicker,
  AppPickerAppEnum,
  Checkbox,
  Menu,
  MenuTop,
  useIsMobile,
} from '@bbl-digital/snorre-v2'
import { AppPicker as USBLAppPicker } from '@bbl-digital/snorre'
import { useSelector } from 'react-redux'
import { getMenu } from 'shared/store/menu/selectors'
import { getMenuIcon } from 'shared/utils/menu/menuIcon'
import MenuLoading from './MenuLoading'
import { getBbl, getBblDisplayNames, getBblSettings } from 'shared/store/bbl'
import { isActiveParent } from 'shared/utils/url'
import MobileContextMenu from 'App/Layout/shared/MobileContextMenu'
import { cn } from 'shared/utils/cn'
import { MenuItem } from './MenuItem'
import { isArendal, isBosor, isUsbl } from 'shared/utils/theme'
import { useTheme } from '@emotion/react'
import { navigationBreakpoint } from 'shared/constants/styles'

interface IProps {
  open: boolean
  logo?: string
  onLogoClick?: () => void
  onClose?: () => void
}

const MenuLayout: React.FC<IProps> = ({ open, logo, onClose }) => {
  const { data, isLoading } = useSelector(getMenu)
  const { data: bbl } = useSelector(getBbl)
  const { navigateToLandingPageWhenBblLogoClickedInPortal } =
    useSelector(getBblSettings)
  const theme = useTheme()
  const { myPageShort, portalShort } = useSelector(getBblDisplayNames)
  const location = useLocation()
  const small = !open
  const isMobile = useIsMobile(navigationBreakpoint)
  const mypageUrl = bbl?.settings?.urls?.myPage
  const [keepExpanded, setKeepExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebarKeepExpanded')
    return stored ? JSON.parse(stored) : false
  })

  useEffect(() => {
    localStorage.setItem('sidebarKeepExpanded', JSON.stringify(keepExpanded))
  }, [keepExpanded])

  const defaultExpanded = data?.find((item) =>
    isActiveParent(item.url, location.pathname)
  )?.url

  const [currentExpandedMenuUrl, setCurrentExpandedMenuUrl] = useState<
    string | null
  >(defaultExpanded)

  const hasDataAndNotLoading = data && !isLoading

  const MainMenuClasses = cn(
    '!w-full gap-1 py-1 !mt-1 !ml-0 overflow-y-scroll max-h-[calc(100vh-270px)] scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-0 box-border !px-[12px]',
    isMobile && 'max-h-[calc(100vh-220px)] '
  )

  return (
    <Menu open={open} className="relative overflow-y-auto md:fixed">
      {!isMobile && (
        <MenuTop
          small={small}
          logo={logo}
          logoHref={
            navigateToLandingPageWhenBblLogoClickedInPortal ? '/' : '/portal'
          }
        >
          {!isUsbl() && (
            <AppPicker
              variant={isArendal() ? 'tempCustomForArendal' : 'dark'}
              small={small}
              withPortal={small}
              selected={{ value: AppPickerAppEnum.Portalen }}
            >
              <AppPicker.App
                label={portalShort}
                value={AppPickerAppEnum.Portalen}
              />
              {mypageUrl && (
                <AppPicker.App
                  label={myPageShort}
                  value={AppPickerAppEnum.MinSide}
                  href={mypageUrl}
                />
              )}
            </AppPicker>
          )}
          {isUsbl() && (
            <div
              className={cn(
                'flex gap-xs my-md w-full  justify-center',
                small && '[&>a>span>span]:text-text-invert'
              )}
            >
              <USBLAppPicker
                small={small}
                href="/"
                label={portalShort}
                icon={<IconBoard color={theme.primary} />}
                lightTheme={false}
                selected
              />

              {mypageUrl && (
                <USBLAppPicker
                  small={small}
                  label={myPageShort}
                  href={mypageUrl}
                  icon={<IconUser size="30px" color="white" />}
                  lightTheme={false}
                  className="mysite-shortcut"
                />
              )}
            </div>
          )}
        </MenuTop>
      )}

      <div
        className={cn(
          'flex flex-col justify-between w-full gap-1 menuwrapper',
          !isMobile && 'h-full'
        )}
        aria-label="Hovedmeny"
      >
        <MainMenu role="menu" small={small} className={MainMenuClasses}>
          {isLoading && <MenuLoading />}
          {hasDataAndNotLoading &&
            data.map((item, index) => {
              return (
                !item.hidden && (
                  <MenuItem
                    item={item}
                    isExpanded={currentExpandedMenuUrl === item.url}
                    setCurrentExpandedMenuUrl={setCurrentExpandedMenuUrl}
                    small={small}
                    icon={getMenuIcon(
                      item.name,
                      isActiveParent(item.url, location.pathname)
                    )}
                    key={index}
                    active={isActiveParent(item.url, location.pathname)}
                    aria-current={isActiveParent(item.url, location.pathname)}
                  />
                )
              )
            })}
          {isMobile && <MobileContextMenu onClose={onClose} />}
        </MainMenu>
        {!isMobile && (
          <div className="flex flex-col items-center justify-center gap-1 pb-[44px] ">
            {open && (
              <Checkbox
                label="Hold meny åpen"
                wrapperClassName={cn(
                  'font-normal text-text-invert [&>div>label]:!font-normal  [&>div>label]:text-nowrap ',
                  (isUsbl() || isBosor()) &&
                    '[&>div>input:checked]:!border-white',
                  isArendal() &&
                    '[&>div>input:checked]:!border-white [&>div>label]:text-surface-portalen-aside-icon-active'
                )}
                checked={keepExpanded}
                onChange={(e) => setKeepExpanded(e.target.checked)}
                aria-label="Hold menyen åpen"
              />
            )}
          </div>
        )}
      </div>
    </Menu>
  )
}

export default MenuLayout
